import * as React from 'react';

import type { TableCardsView } from '@gitbook/doc-core';
import { GridList, type GridListRenderItemInfo, spacing } from '@gitbook/react-uikit';
import { useScreenType } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import type { TableRecordKV } from '../../types';
import type { TableViewProps } from '../types';
import { NewTableRecordCard } from './NewTableRecordCard';
import { TableRecordCard } from './TableRecordCard';

/**
 * Renderer for a table as a cards.
 */
export function TableCardsViewComponent(props: TableViewProps<TableCardsView>): React.ReactElement {
    const {
        nodeKey,
        editor,
        readOnly,
        definition,
        records,
        view,
        selection,
        selections,
        openRecord,
        diff,
    } = props;

    const { screenType } = useScreenType();

    const defaultTextDefinitionId = React.useMemo(() => {
        return view.columns.find((columnId) => definition[columnId].type === 'text') || undefined;
    }, [view.columns, definition]);

    const numColumnsPerScreen = React.useMemo(() => {
        switch (view.cardSize) {
            case 'medium':
                return {
                    mobile: 1,
                    tablet: 2,
                    desktop: 3,
                };
            case 'large':
                return {
                    mobile: 1,
                    tablet: 1,
                    desktop: 2,
                };
            default:
                assertNever(view.cardSize);
        }
    }, [view.cardSize]);

    const renderItem = React.useCallback(
        ({ item, index }: GridListRenderItemInfo<TableRecordKV>) => {
            if (item.key === 'new') {
                return (
                    <NewTableRecordCard
                        editor={editor}
                        nodeKey={nodeKey}
                        defaultTextDefinitionId={defaultTextDefinitionId}
                    />
                );
            }

            const selected = !selection.definitionId && selection.recordId === item.key;
            const otherSelection = selections.find(
                (s) => s.recordId === item.key && !s.definitionId
            );

            return (
                <TableRecordCard
                    nodeKey={nodeKey}
                    editor={editor}
                    readOnly={readOnly}
                    definition={definition}
                    record={item}
                    view={view}
                    diff={diff}
                    selected={selected || !!otherSelection}
                    selectedBy={selected ? undefined : otherSelection?.clientId}
                    isLastInRow={
                        numColumnsPerScreen[screenType] === 1 ||
                        (index + 1) % numColumnsPerScreen[screenType] === 0
                    }
                    beforeOrderIndex={index > 0 ? records[index - 1].value.orderIndex : undefined}
                    afterOrderIndex={
                        index < records.length - 1 ? records[index + 1].value.orderIndex : undefined
                    }
                    onExpand={() => openRecord(item.key)}
                />
            );
        },
        [
            nodeKey,
            editor,
            readOnly,
            definition,
            view,
            selection.recordId,
            !!selection.definitionId,
            defaultTextDefinitionId,
            openRecord,
            selections,
        ]
    );

    return (
        <GridList
            data={readOnly ? records : [...records, { key: 'new' }]}
            renderItem={renderItem}
            numColumnsPerScreen={numColumnsPerScreen}
            columnSpacing={spacing.medium}
            rowSpacing={spacing.medium}
        />
    );
}
